import { to } from "@contentful-api/helpers/to";
import type { ContentfulClientApi, Entry, EntryCollection } from "contentful";
import { createClient as createContentDeliveryClient } from "contentful";

const CACHE_TIME = 300000; // 5 minutes

export function getContentDeliveryClient({ isPreview }: { isPreview: boolean }) {
    const contentfulBaseAuth = {
        spaces: {
            [process.env.CONTENTFUL_BASE_SPACE_ID]: isPreview
                ? process.env.CONTENTFUL_BASE_PREVIEW_ACCESS_TOKEN
                : process.env.CONTENTFUL_BASE_ACCESS_TOKEN,
        },
    };

    return createContentDeliveryClient({
        space: process.env.CONTENTFUL_SPACE_ID || " ",
        accessToken: isPreview
            ? process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN
            : process.env.CONTENTFUL_ACCESS_TOKEN,
        environment: process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || " ",
        host: isPreview ? "preview.contentful.com" : "cdn.contentful.com",
        headers: {
            "x-contentful-resource-resolution": Buffer.from(
                JSON.stringify(contentfulBaseAuth)
            ).toString("base64"),
        },
    });
}

export class CMSWrapper {
    private static readonly instanceMap: Map<boolean, CMSWrapper> = new Map();
    private readonly contentDeliveryClient: ContentfulClientApi;
    private readonly cache = new Map<string, { timestamp: number; data: any }>();
    private readonly cacheTime: number;

    private constructor(isPreview?: boolean) {
        this.contentDeliveryClient = getContentDeliveryClient({ isPreview });
        this.cacheTime = isPreview ? 0 : CACHE_TIME;
    }

    public static getInstance(isPreview?: boolean): CMSWrapper {
        const preview = Boolean(isPreview);
        if (!this.instanceMap.has(preview)) {
            this.instanceMap.set(preview, new CMSWrapper(preview));
        }
        return this.instanceMap.get(preview);
    }

    private getCachedData(key: string) {
        const result = this.cache.get(key);
        const dateNow = Date.now();

        // Returned cached data if it exists and is not expired
        if (result && dateNow - result.timestamp < this.cacheTime) {
            return result.data;
        }

        return null;
    }

    private async fetchAndCacheData(key: string, cmsFunction: () => Promise<any>): Promise<any> {
        const cachedData = this.getCachedData(key);

        if (cachedData) {
            return Promise.resolve(cachedData);
        }

        const { data } = await to(cmsFunction());
        this.cache.set(key, { timestamp: Date.now(), data });
        return data;
    }

    async getEntries<T>(query: any): Promise<EntryCollection<T> | null> {
        const cacheKey = JSON.stringify(query);
        const cmsFunction = () => this.contentDeliveryClient.getEntries<T>(query);

        return this.fetchAndCacheData(cacheKey, cmsFunction);
    }

    async getEntry<T>(id: string, query: any): Promise<Entry<T> | null> {
        const cacheKey = JSON.stringify(id.concat(JSON.stringify(query)));
        const cmsFunction = () => this.contentDeliveryClient.getEntry<T>(id, query);

        return this.fetchAndCacheData(cacheKey, cmsFunction);
    }
}
